import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { BellIcon, MapPinAreaIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";

/** Floating notification bell — subscribes to SSE ("rf-data-change") and
 *  surfaces geofence enter/exit events as toasts + a badge counter. Click
 *  opens a dropdown with the last 20 events. */
export default function NotificationBell() {
  const [events, setEvents] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const seenIdsRef = useRef(new Set());
  const dropdownRef = useRef(null);

  const loadEvents = async () => {
    try {
      const { data } = await api.get("/geofence-events?limit=20");
      const list = Array.isArray(data) ? data : [];
      setEvents(list);
      // On first load, mark existing events as "already seen"
      if (seenIdsRef.current.size === 0) {
        list.forEach((e) => seenIdsRef.current.add(e.id));
      }
    } catch { /* silent */ }
  };

  useEffect(() => {
    loadEvents();

    const onEvt = (e) => {
      const detail = e?.detail;
      if (detail?.entity !== "geofence_events" || detail?.action !== "created") return;
      const extra = detail.extra || {};
      const kind = (extra.kind || "").toUpperCase();
      const driver = extra.driver_name || "A driver";
      const yard = extra.geofence_name || "a yard";
      // Show toast
      toast(
        `${driver} ${kind === "ENTER" ? "entered" : "left"} ${yard}`,
        {
          description: extra.trip_number ? `Trip ${extra.trip_number}` : undefined,
          icon: <MapPinAreaIcon size={18} weight="bold" />,
        }
      );
      // Bump unread + refetch list
      if (detail.id && !seenIdsRef.current.has(detail.id)) {
        seenIdsRef.current.add(detail.id);
        setUnread((u) => u + 1);
      }
      loadEvents();
    };
    window.addEventListener("rf-data-change", onEvt);

    // Close dropdown on outside click
    const onDocClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);

    return () => {
      window.removeEventListener("rf-data-change", onEvt);
      document.removeEventListener("mousedown", onDocClick);
    };
  }, []);

  const toggle = () => {
    if (!open) setUnread(0);
    setOpen((o) => !o);
  };

  const relTime = (iso) => {
    if (!iso) return "";
    const m = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    if (m < 1440) return `${Math.floor(m / 60)}h ago`;
    return `${Math.floor(m / 1440)}d ago`;
  };

  return (
    <div ref={dropdownRef} className="fixed top-4 right-4 z-40" data-testid="notification-bell">
      <button
        onClick={toggle}
        aria-label="Notifications"
        className="relative w-11 h-11 rounded-full bg-white border-2 border-slate-200 hover:border-slate-400 shadow-md flex items-center justify-center transition-colors"
      >
        <BellIcon size={20} weight="bold" className="text-slate-700" />
        {unread > 0 && (
          <span
            data-testid="notification-badge"
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-black flex items-center justify-center border-2 border-white"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div
          data-testid="notification-dropdown"
          className="absolute right-0 mt-2 w-96 bg-white border-2 border-slate-200 rounded-sm shadow-xl overflow-hidden"
        >
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <div className="font-black text-sm uppercase tracking-widest text-slate-700">Geofence events</div>
            <Link
              to="/geofences"
              onClick={() => setOpen(false)}
              className="text-xs text-blue-600 hover:underline font-semibold"
            >View all</Link>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {events.length === 0 ? (
              <div className="px-4 py-6 text-center text-xs text-slate-500 font-mono">
                No events yet — draw a yard on the Geofences page to start tracking.
              </div>
            ) : (
              events.map((e) => (
                <div key={e.id} className="px-4 py-3 border-b border-slate-100 hover:bg-slate-50" data-testid={`bell-event-${e.id}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className={`text-[10px] px-2 py-0.5 rounded-sm font-black uppercase tracking-wider ${
                        e.kind === "enter" ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"
                      }`}
                    >
                      {e.kind}
                    </span>
                    <span className="text-xs text-slate-500 font-mono ml-auto">{relTime(e.at)}</span>
                  </div>
                  <div className="text-sm">
                    <b>{e.driver_name || "Driver"}</b>{" "}
                    {e.kind === "enter" ? "entered" : "left"}{" "}
                    <b>{e.geofence_name}</b>
                  </div>
                  {e.trip_number && (
                    <div className="text-xs text-slate-500 font-mono mt-1">Trip {e.trip_number}</div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
